import { convexQuery } from "@convex-dev/react-query";
import { api } from "@convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const getEmployeeByUserIdRef = api.employees.getEmployeeByUserId;

const locationsGetAllMineRef = api.locations.getAllMine;

const createOrganizationEmployeeRef = api.employees.createOrganizationEmployee;

import { cn } from "@plasma/utils/cn";

import { PinForm } from "../components/auth/pin-form";
import { LocationsTable } from "../components/setup/locations-table";
import { Skeleton } from "../components/ui/skeleton";
import { Spinner } from "../components/ui/spinner";

export const Route = createFileRoute("/setup")({
  component: SetupPage,
});

const PlasmaLogo = ({ className }: { className?: string }) => {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <img
        alt="Plasma logo"
        className="h-full"
        height={64}
        src="/images/plasma-favicon.png"
        width={64}
      />
    </div>
  );
};

function SetupPage() {
  const navigate = useNavigate();
  const { isLoading: isConvexAuthLoading, isAuthenticated: isSignedIn } =
    useConvexAuth();
  const isAuthLoaded = !isConvexAuthLoading;
  const [isPinSet, setIsPinSet] = useState(false);
  const [isCheckingSetup, setIsCheckingSetup] = useState(true);

  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (isAuthLoaded && !isSignedIn) {
      void navigate({ to: "/sign-in" });
    }
  }, [isAuthLoaded, isSignedIn, navigate]);

  // Check if the CURRENT USER has an employee profile
  // This doesn't require permissions - unlike employees.getAll which needs viewEmployees
  const { data: currentEmployee } = useQuery({
    ...convexQuery(
      getEmployeeByUserIdRef,
      isAuthLoaded && isSignedIn ? {} : "skip"
    ),
  });
  const hasEmployee = currentEmployee !== undefined && currentEmployee !== null;

  const { data: locations } = useQuery({
    ...convexQuery(
      locationsGetAllMineRef,
      isAuthLoaded && isSignedIn ? {} : "skip"
    ),
  });

  const createOrganizationEmployeeMutation = useMutation({
    mutationFn: (data: { pin: string }) =>
      convexClient.mutation(createOrganizationEmployeeRef, data),
  });

  useEffect(() => {
    // Wait for currentEmployee query to complete (undefined means still loading)
    if (currentEmployee === undefined || locations === undefined) {
      return;
    }

    const hasLocations = locations.length > 0;

    // If user has employee profile and locations, setup is complete
    if (hasEmployee && hasLocations) {
      void navigate({ to: "/" });
      return;
    }

    // If user has employee profile but no locations, show location form
    if (hasEmployee && !hasLocations) {
      setIsPinSet(true);
      setIsCheckingSetup(false);
      return;
    }

    // No employee profile yet - show PIN form
    setIsCheckingSetup(false);
  }, [currentEmployee, hasEmployee, locations, navigate]);

  const onPinSubmit = async (pin: string) => {
    try {
      await createOrganizationEmployeeMutation.mutateAsync({ pin });
      // Track onboarding step completion in PostHog
      ProductEvents.onboardingStepCompleted("pin_created", 1);
      setIsPinSet(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create employee. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Show loading while checking auth
  if (!isAuthLoaded) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  // Don't render if not signed in (will redirect)
  if (!isSignedIn) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (isCheckingSetup) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "bg-background text-foreground flex min-h-dvh justify-center",
        isPinSet ? "pt-16" : "items-center"
      )}
    >
      {isPinSet ? <LocationsForm /> : <FullPinForm onSubmit={onPinSubmit} />}
    </div>
  );
}

const FullPinForm = ({ onSubmit }: { onSubmit: (pin: string) => void }) => {
  return (
    <div className="flex max-w-115 flex-col items-center gap-6 pb-10">
      <div className="flex flex-col items-center gap-6">
        <PlasmaLogo className="text-primary h-16" />
        <div className="flex flex-col gap-3">
          <h1 className="h2 font-heading text-foreground text-center leading-tight text-balance">
            Create your 4 digit PIN
          </h1>
          <p className="text-foreground text-center">
            You'll use this PIN to log into the Plasma POS app for all of your
            locations.
          </p>
        </div>
      </div>
      <PinForm onSubmit={onSubmit} submitButtonText="Create my POS account" />
    </div>
  );
};

const LocationsForm = () => {
  return (
    <div className="w-full max-w-6xl space-y-10">
      <div className="flex flex-col items-center gap-6">
        <PlasmaLogo className="text-primary h-16" />
        <div className="flex flex-col gap-3">
          <h1 className="h2 font-heading text-foreground text-center leading-tight text-balance">
            Add your locations
          </h1>
          <p className="text-foreground text-center">
            Add the details of your locations, you can edit or add more later.
          </p>
        </div>
        <LocationsTable />
      </div>
    </div>
  );
};
